import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { LinkIcon, ChevronDownIcon, LinkedinIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { advisors, Advisor } from '../data/advisors';
function AdvisorCard({
  advisor,
  index
}: {
  advisor: Advisor;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [bioOpen, setBioOpen] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const esTitles: Record<string, string> = {
    DB: 'Miembro de la Junta Escolar',
    EC: 'Presidenta y CEO Emérita',
    SF: 'Fundador',
    TK: 'Senador de EE.UU.',
    MF: 'Secretario de Salud y Recursos Humanos',
    AR: 'CEO — Jubilado',
    AF: 'Actriz y Directora',
    JQ: 'Sheriff'
  };
  const esBios: Record<string, string> = {
    DB: 'Diana Brown forma parte de la Junta Escolar de la Ciudad de Manassas, donde es una defensora visible de escuelas públicas sólidas, entornos de aprendizaje inclusivos y la toma de decisiones centrada en los estudiantes. Fue elegida en 2025 para un mandato de cuatro años, y también es Vicepresidenta de la Junta del Northern Virginia Community College. Inmigrante de Honduras y educadora galardonada, Brown aporta una profunda experiencia en liderazgo de educación especial y ha sido reconocida a nivel estatal por su trabajo apoyando a estudiantes y maestros.',
    EC: 'Eva Callejas es una líder cívica hondureña que anteriormente se desempeñó como Directora Ejecutiva (y figura principal) de Honduras Global, una red de conocimiento de la diáspora enfocada en conectar el talento hondureño en el exterior con iniciativas de desarrollo en el país. En ese rol, ayudó a construir alianzas con actores gubernamentales y educativos para avanzar la innovación, la tecnología y las oportunidades para las comunidades hondureñas.',
    SF: 'Shin Fujiyama es un filántropo japonés-americano y cofundador/director ejecutivo de Students Helping Honduras, una organización sin fines de lucro enfocada en ampliar el acceso a la educación. Dedicó su vida a construir escuelas y apoyar comunidades en todo el país a través de la visión de Mil Escuelas.',
    TK: 'Tim Kaine sirve como Senador de EE.UU. por Virginia, aportando décadas de servicio público como ex Gobernador y Vicegobernador del Estado. Sus lazos con Honduras son profundamente personales: al inicio de su carrera vivió en El Progreso, Honduras, trabajando con misioneros jesuitas en una escuela vocacional.',
    MF: 'Marvin B. Figueroa sirve como Secretario de Salud y Recursos Humanos de Virginia, liderando el trabajo del Estado para fortalecer la salud pública, ampliar el acceso a la atención médica y mejorar la prestación de servicios humanos. Originario de La Ceiba, Honduras.',
    AR: 'Alfredo Rivera es un ejecutivo empresarial nacido en Honduras y líder de larga trayectoria en Coca-Cola que ascendió a los rangos regionales más altos de la compañía después de más de tres décadas en el sistema de Coca-Cola.',
    AF: 'America Ferrera es una actriz, productora y activista galardonada cuyas raíces hondureñas han moldeado durante mucho tiempo su voz pública y su defensa. Nacida en Los Ángeles de padres de Tegucigalpa, Honduras.',
    JQ: 'Jose Quiroz sirve como Sheriff del Condado de Arlington, Virginia, liderando la Oficina del Sheriff en sus responsabilidades de operaciones carcelarias, seguridad del tribunal y servicios de seguridad pública orientados a la comunidad.'
  };
  const displayTitle = isEs ? esTitles[advisor.initials] || advisor.title : advisor.title;
  const displayBio = isEs ? esBios[advisor.initials] || advisor.bio : advisor.bio;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 50,
    rotate: index % 2 === 0 ? -2 : 2
  }} animate={inView ? {
    opacity: 1,
    y: 0,
    rotate: 0
  } : {}} transition={{
    duration: 0.7,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 ${advisor.borderColor} group card-hover flex flex-col`} data-id="element-203">
      <div className="p-6 flex flex-col flex-1" data-id="element-204">
        {/* Avatar — unified layout for all cards */}
        <div className="flex items-start gap-4 mb-4" data-id="element-205">
          {advisor.photo ? <motion.div whileHover={{
          scale: 1.05
        }} className={`w-40 h-40 rounded-2xl bg-gradient-to-br ${advisor.gradient} flex-shrink-0 shadow-lg overflow-hidden`} data-id="element-206">
              <img src={advisor.photo} alt={advisor.name} className="w-full h-full object-cover object-[center_30%]" style={undefined} data-id="element-207" />
            </motion.div> : <motion.div whileHover={{
          scale: 1.05,
          rotate: 5
        }} className={`w-40 h-40 rounded-2xl bg-gradient-to-br ${advisor.gradient} flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden`} data-id="element-208">
              <span className="font-bebas text-white text-4xl tracking-wider" data-id="element-209">
                {advisor.initials}
              </span>
            </motion.div>}
          <div className="flex-1 min-w-0" data-id="element-210">
            <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-211">
              {advisor.name}
            </h3>
            <p className={`font-nunito font-bold text-sm ${advisor.accentColor} uppercase tracking-wider mt-1`} data-id="element-212">
              {displayTitle}
            </p>
            <p className="font-nunito text-xs text-haa-skyblue font-semibold tracking-wide mt-0.5" data-id="element-213">
              {advisor.subtitle}
            </p>
          </div>
        </div>

        {/* Bio Dropdown Toggle */}
        <button onClick={() => setBioOpen(!bioOpen)} aria-expanded={bioOpen} className={`w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl mb-1 text-left transition-colors duration-200 ${bioOpen ? 'bg-gray-50' : 'hover:bg-gray-50'}`} data-id="element-214">
          <span className={`font-nunito font-semibold text-sm ${advisor.accentColor}`} data-id="element-215">
            {bioOpen ? isEs ? 'Ocultar Biografía' : 'Hide Bio' : isEs ? 'Leer Biografía' : 'Read Bio'}
          </span>
          <motion.div animate={{
          rotate: bioOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-br ${advisor.gradient}`} data-id="element-216">
            <ChevronDownIcon className="w-3.5 h-3.5 text-white" data-id="element-217" />
          </motion.div>
        </button>

        {/* Animated Bio */}
        <AnimatePresence initial={false} data-id="element-218">
          {bioOpen && <motion.div key="bio" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="overflow-hidden" data-id="element-219">
              <p className="font-nunito text-gray-600 text-sm leading-relaxed pt-2 pb-4 px-1" data-id="element-220">
                {displayBio}
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Link button */}
        <div className="flex items-center gap-2 pt-4 mt-auto border-t border-gray-100" data-id="element-221">
          {advisor.linkedin && <motion.a href={advisor.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${advisor.name} LinkedIn`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${advisor.gradient} flex items-center justify-center`} data-id="element-222">
              <LinkedinIcon className="w-4 h-4 text-white" data-id="element-223" />
            </motion.a>}
          {advisor.link && <motion.a href={advisor.link} target="_blank" rel="noopener noreferrer" aria-label={`${advisor.name} profile link`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${advisor.gradient} flex items-center justify-center`} data-id="element-224">
              <LinkIcon className="w-4 h-4 text-white" data-id="element-225" />
            </motion.a>}
        </div>
      </div>
    </motion.article>;
}
export function AdvisoryCouncil() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <section id="advisory-council" className="bg-white py-20 lg:py-28" aria-labelledby="advisory-heading" data-id="element-226">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-14" data-id="element-228">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-229">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-230">
              {isEs ? 'Nuestros Asesores' : 'Our Advisors'}
            </span>
            <h2 id="advisory-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-231">
              {isEs ? 'Consejo de Asesoria' : 'Advisory Council'}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-232">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-233" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-234" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-235" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-236">
              {isEs ? 'Líderes experimentados y expertos que guían nuestra misión y ayudan a dar forma al futuro de la comunidad hondureño-americana.' : 'Experienced leaders and experts who guide our mission and help shape the future of the Honduran-American community.'}
            </p>
          </motion.div>
        </div>

        {/* Advisors Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-237">
          {advisors.map((advisor, i) => <AdvisorCard key={`${advisor.name}-${i}`} advisor={advisor} index={i} data-id="element-238" />)}
        </div>
      </div>
    </section>;
}